import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Clock,
  Zap,
  RefreshCw,
  Search,
  X,
  ArrowUpDown,
  Filter,
  Check,
  Loader2,
} from "lucide-react";
import betaAPI from "v2/api";
import useInfiniteScroll from "v2/hooks/useInfiniteScroll";
import { t, dateLocale } from "i18n";

const SessionRowSkeleton = () => (
  <div className="flex animate-pulse items-center gap-3 border-b border-[#F3F4F6] px-4 py-3">
    <div className="h-7 w-7 rounded-lg bg-[#E5E7EB]" />
    <div className="flex-1">
      <div className="mb-1.5 h-3 w-32 rounded bg-[#E5E7EB]" />
      <div className="h-2.5 w-20 rounded bg-[#E5E7EB]" />
    </div>
    <div className="h-2.5 w-24 rounded bg-[#E5E7EB]" />
  </div>
);

const formatFullDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(dateLocale("en-GB"), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatRelativeDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSecs < 60) return t("common.time.justNow");
  if (diffMins < 60)
    return t("team.sessionsSection.minsAgo", { count: diffMins });
  if (diffHours < 24) return t("common.time.hoursAgo", { count: diffHours });
  if (diffDays === 1) return t("common.time.yesterday");
  if (diffDays < 7) return t("common.time.daysAgo", { count: diffDays });
  if (diffWeeks < 5)
    return t("team.sessionsSection.weeksAgo", { count: diffWeeks });
  if (diffMonths < 12)
    return t("team.sessionsSection.monthsAgo", { count: diffMonths });

  return date.toLocaleDateString(dateLocale("en-GB"), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const SORT_OPTIONS = [
  { value: "date-desc", labelKey: "team.sessionsSection.sort.dateDesc" },
  { value: "date-asc", labelKey: "team.sessionsSection.sort.dateAsc" },
  { value: "name-asc", labelKey: "team.sessionsSection.sort.nameAsc" },
  { value: "name-desc", labelKey: "team.sessionsSection.sort.nameDesc" },
  { value: "impacts-desc", labelKey: "team.sessionsSection.sort.impactsDesc" },
  { value: "impacts-asc", labelKey: "team.sessionsSection.sort.impactsAsc" },
];

const TYPE_OPTIONS = [
  { value: "all", labelKey: "team.sessionsSection.typeFilter.all" },
  { value: "training", labelKey: "team.sessionsSection.typeFilter.training" },
  { value: "match", labelKey: "team.sessionsSection.typeFilter.match" },
];

// UI sort keys → the API's ordering values. "Most Recent" follows upload
// time server-side, so the list agrees with the activity feed.
const ORDERING_MAP = {
  "date-desc": "recent",
  "date-asc": "oldest",
  "name-asc": "name",
  "name-desc": "-name",
  "impacts-desc": "-impacts",
  "impacts-asc": "impacts",
};

const PAGE_SIZE = 30;

// `embedded`: render inside another card (TeamFeedCard) — no own card chrome,
// no height cap (the surrounding pane scrolls), title replaced by the count.
// `player` ({ id, name }): list only that athlete's sessions, each led by
// their own numbers, with the team's as context; "Show all team sessions"
// switches back without clearing the athlete (SPO-1537).
const TeamSessionsSection = ({
  className = "",
  embedded = false,
  player = null,
}) => {
  const { t } = useTranslation();
  const { workspaceId, teamId } = useParams();
  const navigate = useNavigate();
  const [showAllSessions, setShowAllSessions] = useState(false);
  useEffect(() => setShowAllSessions(false), [player?.id]);
  const playerFilter = player && !showAllSessions ? player.id : "";
  const firstName = player?.name?.split(" ")[0];

  // Search, sort, filter state — all pushed to the server, since the list is
  // paginated and client-side filtering would only see the loaded pages.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortKey, setSortKey] = useState("date-desc");
  const [sortOpen, setSortOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);

  const searchInputRef = useRef(null);
  const sortRef = useRef(null);
  const filterRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchFn = useCallback(
    async ({ page, pageSize, search, type, ordering, player: playerId }) => {
      const response = await betaAPI.getTeamSessions({
        workspaceId,
        teamId,
        queryParams: {
          page,
          page_size: pageSize,
          ...(search && { search }),
          ...(type && { type }),
          ...(ordering && { ordering }),
          ...(playerId && { player: playerId }),
        },
      });
      const payload = response.data?.sessions;
      // Older API builds return a flat array of every session; treat it as
      // one complete page so the list still renders.
      if (Array.isArray(payload)) {
        return { data: { results: payload, total_count: payload.length } };
      }
      return { data: payload || { results: [] } };
    },
    [workspaceId, teamId]
  );

  const {
    items: sessions,
    loading,
    totalCount,
    error,
    lastItemRef,
    refresh,
  } = useInfiniteScroll({
    fetchFn,
    search: debouncedSearch,
    pageSize: PAGE_SIZE,
    extraParams: {
      type: typeFilter === "all" ? "" : typeFilter,
      ordering: ORDERING_MAP[sortKey] || "recent",
      player: playerFilter,
    },
  });

  // Focus input when search opens
  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchOpen]);

  // Close dropdowns on outside click or Escape
  useEffect(() => {
    if (!filterOpen && !sortOpen) return;

    const handleClickOutside = (e) => {
      if (
        filterOpen &&
        filterRef.current &&
        !filterRef.current.contains(e.target)
      ) {
        setFilterOpen(false);
      }
      if (sortOpen && sortRef.current && !sortRef.current.contains(e.target)) {
        setSortOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setFilterOpen(false);
        setSortOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [filterOpen, sortOpen]);

  const handleCloseSearch = () => {
    setSearchOpen(false);
    setSearchTerm("");
  };

  const handleRetry = () => {
    refresh();
  };

  const sortActive = sortKey !== "date-desc";
  const filterActive = typeFilter !== "all";
  const filtersInPlay = filterActive || Boolean(debouncedSearch);
  const initialLoading = loading && sessions.length === 0;

  return (
    <div
      className={`flex flex-col ${
        embedded ? "" : "rounded-xl border border-[#F3F4F6] bg-white"
      } ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <div className="flex items-center gap-2">
          {embedded ? (
            <span className="text-xs text-[#6B7280]">
              {playerFilter && (
                <span className="font-medium text-[#111827]">
                  {t("team.sessionsSection.playerTitle", { name: firstName })}
                  {" · "}
                </span>
              )}
              {!initialLoading && !error
                ? t("team.sessionsSection.sessionCount", { count: totalCount })
                : t("team.sessionsSection.title")}
            </span>
          ) : (
            <h3 className="text-sm font-semibold text-[#111827]">
              {t("team.sessionsSection.title")}
            </h3>
          )}
          {!embedded && !initialLoading && !error && (
            <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-xs font-medium text-[#6B7280]">
              {totalCount}
            </span>
          )}
        </div>
        {player && (
          <button
            type="button"
            onClick={() => setShowAllSessions((all) => !all)}
            className="ml-auto mr-2 rounded text-[11px] font-medium text-[#E07A3A] outline-none hover:text-[#B85E25] focus-visible:ring-2 focus-visible:ring-[#E07A3A]/40"
          >
            {showAllSessions
              ? t("team.sessionsSection.onlyPlayerSessions", {
                  name: firstName,
                })
              : t("team.sessionsSection.showAllSessions")}
          </button>
        )}
        {!initialLoading &&
          !error &&
          (sessions.length > 0 || filtersInPlay) && (
            <div className="flex items-center gap-1.5">
              <div className="relative" ref={sortRef}>
                <button
                  onClick={() => setSortOpen((prev) => !prev)}
                  className={`flex h-7 w-7 items-center justify-center rounded-md border transition duration-150 ${
                    sortActive || sortOpen
                      ? "border-[#E07A3A] bg-[#FEF7F3] text-[#E07A3A]"
                      : "border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#D1D5DB] hover:bg-[#F9FAFB] hover:text-[#374151]"
                  }`}
                  title={t("team.sessionsSection.sortTitle")}
                >
                  <ArrowUpDown size={14} />
                </button>
                {sortOpen && (
                  <div
                    className="absolute right-0 top-full z-10 mt-1 w-40 overflow-hidden rounded-lg border border-[#E5E7EB] bg-white shadow-lg"
                    style={{
                      animation: "dropdownFadeIn 150ms ease-out",
                    }}
                  >
                    {SORT_OPTIONS.map((opt) => {
                      const selected = sortKey === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => {
                            setSortKey(opt.value);
                            setSortOpen(false);
                          }}
                          className={`flex w-full items-center justify-between px-3 py-2 text-xs transition duration-150 ${
                            selected
                              ? "bg-[#FEF3E7] font-medium text-[#E07A3A]"
                              : "text-[#374151] hover:bg-[#F9FAFB]"
                          }`}
                        >
                          {t(opt.labelKey)}
                          {selected && <Check size={12} />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <button
                onClick={() =>
                  searchOpen ? handleCloseSearch() : setSearchOpen(true)
                }
                className={`flex h-7 w-7 items-center justify-center rounded-md border transition duration-150 ${
                  searchOpen
                    ? "border-[#E07A3A] bg-[#FEF7F3] text-[#E07A3A]"
                    : "border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#D1D5DB] hover:bg-[#F9FAFB] hover:text-[#374151]"
                }`}
                title={t("team.sessionsSection.searchTitle")}
              >
                <Search size={14} />
              </button>
              <div className="relative" ref={filterRef}>
                <button
                  onClick={() => setFilterOpen((prev) => !prev)}
                  className={`flex h-7 w-7 items-center justify-center rounded-md border transition duration-150 ${
                    filterActive || filterOpen
                      ? "border-[#E07A3A] bg-[#FEF7F3] text-[#E07A3A]"
                      : "border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#D1D5DB] hover:bg-[#F9FAFB] hover:text-[#374151]"
                  }`}
                  title={t("team.sessionsSection.filterTitle")}
                >
                  <Filter size={14} />
                </button>
                {filterOpen && (
                  <div
                    className="absolute right-0 top-full z-10 mt-1 w-40 overflow-hidden rounded-lg border border-[#E5E7EB] bg-white shadow-lg"
                    style={{
                      animation: "dropdownFadeIn 150ms ease-out",
                    }}
                  >
                    {TYPE_OPTIONS.map((opt) => {
                      const selected = typeFilter === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => {
                            setTypeFilter(opt.value);
                            setFilterOpen(false);
                          }}
                          className={`flex w-full items-center justify-between px-3 py-2 text-xs transition duration-150 ${
                            selected
                              ? "bg-[#FEF3E7] font-medium text-[#E07A3A]"
                              : "text-[#374151] hover:bg-[#F9FAFB]"
                          }`}
                        >
                          {t(opt.labelKey)}
                          {selected && <Check size={12} />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
      </div>

      {/* Expandable Search Bar */}
      <div
        className="overflow-hidden transition-all duration-200"
        style={{
          maxHeight: searchOpen ? "52px" : "0px",
          opacity: searchOpen ? 1 : 0,
        }}
      >
        <div className="relative border-b border-[#F3F4F6] px-4 py-2">
          <Search
            size={14}
            className="absolute left-7 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
          />
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("team.sessionsSection.searchPlaceholder")}
            className="w-full rounded-lg border border-[#E5E7EB] py-1.5 pl-8 pr-8 text-xs text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 focus:border-[#E07A3A] focus:shadow-[0_0_0_3px_rgba(224,122,58,0.1)]"
            onKeyDown={(e) => {
              if (e.key === "Escape") handleCloseSearch();
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-7 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Sessions list */}
      <div
        className={embedded ? "flex-1" : "max-h-[416px] flex-1 overflow-y-auto"}
      >
        {initialLoading ? (
          <>
            <SessionRowSkeleton />
            <SessionRowSkeleton />
            <SessionRowSkeleton />
          </>
        ) : error ? (
          <div className="flex flex-col items-center justify-center px-6 py-8">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#FEF2F2]">
              <Calendar size={20} className="text-[#DC2626]" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("team.sessionsSection.loadFailed")}
            </h3>
            <p className="mb-3 text-center text-xs text-[#6B7280]">{error}</p>
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
            >
              <RefreshCw size={12} />
              {t("common.actions.retry")}
            </button>
          </div>
        ) : sessions.length === 0 && !filtersInPlay ? (
          <div className="flex flex-col items-center justify-center px-6 py-8">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#FEF3E7] to-[#FDE8D3]">
              <Calendar size={22} className="text-[#E07A3A]" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("team.sessionsSection.emptyTitle")}
            </h3>
            <p className="max-w-[200px] text-center text-xs leading-relaxed text-[#6B7280]">
              {t("team.sessionsSection.emptyBody")}
            </p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-8">
            <Search size={20} className="mb-2 text-[#9CA3AF]" />
            <p className="text-xs text-[#6B7280]">
              {t("team.sessionsSection.noMatches")}
            </p>
          </div>
        ) : (
          sessions.map((session, index) => {
            const isMatch =
              session.type === "match" || session.session_type === "match";
            const Icon = isMatch ? Zap : Clock;
            const iconBg = isMatch ? "bg-[#FEF3E7]" : "bg-[#F3F4F6]";
            const iconColor = isMatch ? "text-[#E07A3A]" : "text-[#6B7280]";

            return (
              <div
                key={session.id}
                ref={index === sessions.length - 1 ? lastItemRef : undefined}
                onClick={() =>
                  navigate(
                    `/v2/${workspaceId}/team/${teamId}/session/${session.id}`
                  )
                }
                className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition duration-150 hover:bg-[#FAFAFA] ${
                  index !== sessions.length - 1
                    ? "border-b border-[#F3F4F6]"
                    : ""
                }`}
              >
                {/* Type icon */}
                <div
                  className={`flex h-7 w-7 flex-shrink-0 items-center justify-center self-start rounded-lg ${iconBg}`}
                >
                  <Icon size={14} className={iconColor} />
                </div>

                {/* Name, date, and stats */}
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-[#111827]">
                    {session.name ||
                      session.session_name ||
                      t("team.sessionsSection.untitled")}
                  </p>
                  <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[#9CA3AF]">
                    <span
                      title={formatFullDate(session.created_at || session.date)}
                    >
                      {formatRelativeDate(session.created_at || session.date)}
                    </span>
                    {playerFilter ? (
                      <>
                        <span className="text-[#D1D5DB]">&middot;</span>
                        <span className="font-medium text-[#111827]">
                          {session.player_load != null
                            ? t("team.sessionsSection.playerImpactsAndLoad", {
                                impacts: session.player_impacts ?? 0,
                                load: Math.round(
                                  session.player_load
                                ).toLocaleString(),
                              })
                            : t("team.sessionsSection.impactsOnly", {
                                impacts: session.player_impacts ?? 0,
                              })}
                        </span>
                      </>
                    ) : null}
                    {!playerFilter &&
                      (session.total_impacts != null ||
                        session.assignment_count != null) && (
                        <>
                          <span className="text-[#D1D5DB]">&middot;</span>
                          <span className="text-[#6B7280]">
                            {session.total_impacts != null &&
                            session.assignment_count != null
                              ? t("team.sessionsSection.impactsAndPlayers", {
                                  impacts: session.total_impacts,
                                  players: session.assignment_count,
                                })
                              : session.total_impacts != null
                              ? t("team.sessionsSection.impactsOnly", {
                                  impacts: session.total_impacts,
                                })
                              : t("team.sessionsSection.playersOnly", {
                                  players: session.assignment_count,
                                })}
                          </span>
                        </>
                      )}
                  </div>
                  {playerFilter && session.total_impacts != null && (
                    <p className="mt-0.5 text-[11px] text-[#9CA3AF]">
                      {t("team.sessionsSection.teamContext", {
                        impacts: session.total_impacts,
                        players: session.assignment_count ?? 0,
                      })}
                    </p>
                  )}
                </div>

                {/* Zero-data marker — sessions that were recorded but captured
                    no impacts, so they're easy to spot in the list. */}
                {(playerFilter
                  ? session.player_impacts === 0
                  : session.total_impacts === 0) && (
                  <span className="flex-shrink-0 self-center rounded-full border border-[#F0F0F0] bg-[#F9FAFB] px-2 py-0.5 text-[10px] font-medium text-[#9CA3AF]">
                    {t("team.sessionsSection.noImpacts")}
                  </span>
                )}
              </div>
            );
          })
        )}
        {!initialLoading && loading && sessions.length > 0 && (
          <div className="flex items-center justify-center py-3">
            <Loader2 size={16} className="animate-spin text-[#E07A3A]" />
          </div>
        )}
      </div>

      {/* Dropdown animation keyframes */}
      <style>{`
        @keyframes dropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default TeamSessionsSection;
